/*       */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { muiTheme } from 'app/themes/materialUi';
import { connect } from 'react-redux';
import { showToastr } from 'store/actions/app/appActions';

import { Typography, Grid, FormControl, FormHelperText, IconButton, MdiIcon } from '@mic3/platform-ui';
import { bind, memoize } from 'app/utils/decorators/decoratorUtils';
import { getFieldByType } from 'app/utils/designer/form/fieldUtils';
import { set } from 'app/utils/immutable/Immutable';
import { LOAD_CLASSIFICATION } from 'store/actions/classifications/classificationsActions';
import Link from 'app/components/atoms/Link/HyperLink';
import { appendHttp } from 'app/utils/string/string-utils';
import ModalDialog from 'app/components/organisms/ModalDialog/ModalDialog';

const ModalDialogStyled = styled(ModalDialog)`
    & .MuiDialog-paper {
        height: ${({ height }) => (height ? height : window.innerHeight)}px;
        width: ${({ width }) => (width ? width : window.innerWidth)}px;
    }

    & .MuiDialog-paperWidthMd {
        max-width: ${({ width }) => (width ? width : window.innerWidth)}px;
    }
`;

const Row = styled(Grid)`
    width: 100% !important;
    padding: 8px 0;
`;

const IconButtonStyled = styled(IconButton)`
    ${({ disabled, theme }) => 
    disabled 
        ? `& .MuiIcon-root { color: ${theme.material.colors.text.caption} !important; }`
        : ''
    }
`;

const FieldDivider = styled.div`
    width: 10px;
`;

const TickIcon = styled.div`
    position: relative;
    top: 1.7rem;
`;

const Flex = styled.div`
    display: flex;
`;

/**
 *
 */
class Hyperlink extends PureComponent {
    static propTypes = {
        onChange: PropTypes.func,
        onMouseDown: PropTypes.func,
        lastActionError: PropTypes.bool,
        lastActionType: PropTypes.string,
        variant: PropTypes.oneOf(['contained', 'text']),
    };

    static defaultProps = {
        variant: 'contained',
    };

    constructor(props) {
        super(props);
        this.state = {
            showTextFields: !!props.hideTickIcon, // If a tick icon is shown then changes would be sent to form only when that icon is clicked, if its hidden parent would be getting real time changes
            value: props.value || {},
            showDialog: false,
            dialogUrl: '',
        };
    }

    componentDidUpdate(prevProps) {
        const { lastActionType, lastActionError, value, hideTickIcon } = this.props;
        if (!hideTickIcon && this.state.showTextFields && !lastActionError && lastActionType === LOAD_CLASSIFICATION) {
            this.setState({ showTextFields: false });
        }
        if (!hideTickIcon && value !== prevProps.value) {
            this.setState({ value });
        }
    }

    @bind
    changeValue(event) {
        const { value: changedValue, name } = event;
        const { hideTickIcon } = this.props;
        let { value } = this.state;
        value = set(value, name, changedValue);
        this.setState({ showTextFields: true, value }, () => {
            const { value: latestValue } = this.state;
            if(hideTickIcon) {
                this.props.onChange({ target: { name: this.props.name, value: latestValue } });
            }
        });
       
    }

    @bind
    toggleTextField(event) {
        event.stopPropagation();
        this.setState(
            prevState => ({ showTextFields: !prevState.showTextFields }),
            () => {
                this.props.onChange({ target: { name: this.props.name, value: this.state.value } });
            }
        );
    }
    @bind
    openLink(e, url) {
        e.preventDefault();
        e.stopPropagation();
        const { target, windowHeight, windowWidth } = this.props;
        const width = windowWidth && windowWidth <= window.innerWidth ? windowWidth : window.innerWidth;
        const height = windowHeight && windowHeight <= window.innerHeight ? windowHeight : window.innerHeight;
        if (url) {
            const validUrl = appendHttp(url);
            switch (target) {
                case '_blank':
                    window.open(validUrl, '_blank');
                    break;
                case '_self':
                    window.location.href = validUrl;
                    break;
                case 'newWindow':
                    const left = Math.floor((window.innerWidth - width) / 2);
                    const top = Math.floor((window.innerHeight - height) / 2);
                    window.open(validUrl, '_blank', `width=${width},height=${height},top=${top},left=${left},scrollbars=yes`);
                    break;
                case 'dialog':
                    this.setState({
                        showDialog: true,
                        dialogUrl: validUrl,
                    });
                    break;
                default:
                    window.open(validUrl);
                    break;
            }
        } else {
            this.props.showToastr({ severity: 'warning', detail: 'Please enter the valid link.' });
        }
    }

    @bind
    @memoize()
    openDialog(dialogUrl) {
        const { dialogHeight, dialogWidth } = this.props;
        const width = dialogWidth && dialogWidth <= window.innerWidth ? dialogWidth : window.innerWidth;
        const height = dialogHeight && dialogHeight <= window.innerHeight ? dialogHeight : window.innerHeight;
        return (
            <ModalDialogStyled onClose={this.closeDialog} width={width} height={height}>
                <iframe src={dialogUrl} title="Dialog Content" style={{ width: '100%', height: `calc(${height}px - ${height * 0.16}px)` }}></iframe>
            </ModalDialogStyled>
        );
    }

    @bind
    validateLinkField(val) {
        const { text } = this.state.value || {};
        if (val && !text) {
            return true;
        }
        return !text;
    }

    @bind
    validateTextField(val) {
        const { link } = this.state.value || {};
        if (val && !link) {
            return true;
        }
        return !link;
    }

    @bind
    _buildInput() {
        const { error, helperText, label, required, onMouseDown, disabled, hideTickIcon } = this.props;
        const { value } = this.state;
        const { text, link } = value || {};
        return (
            <Row container direction="column" justify="flex-start" alignItems="stretch" onMouseDown={onMouseDown}>
                {label && <Typography variant="caption">{label}</Typography>}
                <Flex>
                    {getFieldByType('text', {
                        name: 'text',
                        value: text,
                        label: 'Text',
                        placeholder: 'Text',
                        changeData: this.changeValue,
                        clearable: false,
                        error: error && this.validateLinkField(link),
                        required,
                        disabled,
                    })}
                    <FieldDivider />
                    {getFieldByType('text', {
                        name: 'link',
                        value: link,
                        label: 'Link',
                        placeholder: 'Link',
                        changeData: this.changeValue,
                        clearable: false,
                        error: error && this.validateTextField(text),
                        required,
                        disabled,
                    })}
                    {!hideTickIcon ? 
                        <TickIcon>
                            <IconButtonStyled onClick={this.toggleTextField} disabled={disabled || !link || !text} >
                                <MdiIcon name="check" color={muiTheme.colors.text.secondary} />
                            </IconButtonStyled>
                        </TickIcon> : null }
                </Flex>
                {helperText && (
                    <FormControl error={error}>
                        <FormHelperText>{helperText}</FormHelperText>
                    </FormControl>
                )}
            </Row>
        );
    }
    @bind
    closeDialog() {
        this.setState({
            showDialog: false,
            dialogUrl: '',
        });
    }

    /**
     * @override
     */
    render() {
        const { label, disabled, variant } = this.props;
        const { showTextFields, value, showDialog, dialogUrl } = this.state;
        const { text, link } = value || {};

        if (variant === 'text') {
            return <Link text={value.text} url={appendHttp(value.link)} />;
        }
        if ((!showTextFields || disabled) && link && text) {
            return (
                <Grid container direction="column" alignItems="stretch">
                    {label && <Typography variant="caption">{label}</Typography>}
                    <Flex>
                        {showDialog && this.openDialog(dialogUrl)}
                        {getFieldByType('button', {
                            label: text,
                            variant: 'text',
                            onClick: e => this.openLink(e, link),
                            style: { width: '100%' },
                        })}
                        {!disabled ? (
                            <IconButton onClick={this.toggleTextField} disabled={!link || !text} style={{marginRight: '15px'}}>
                                <MdiIcon name="pencil" color={muiTheme.colors.text.secondary} />
                            </IconButton>
                        ) : null}
                    </Flex>
                </Grid>
            );
        }
        return this._buildInput();
    }
}

export default connect(
    state => ({
        lastActionType: state.global.lastActionType,
        lastActionError: state.global.lastActionError,
    }),
    { showToastr }
)(Hyperlink);
